import React from "react";
import { VISIBLE_TITLES } from "../data/titles";

// The mosaic only shows actual posters — no empty black placeholders.
const MOSAIC_TITLES = VISIBLE_TITLES.filter((t) => Boolean(t.poster));

// A single 9:16 tile — clean, edge-to-edge, no transparency stacking.
const Tile = ({ t }) => (
  <div
    className="relative w-full block"
    style={{
      aspectRatio: "9 / 16",
      background: "#0d0d0d",
      overflow: "hidden",
    }}
  >
    <img
      src={t.poster}
      alt=""
      aria-hidden
      draggable={false}
      loading="eager"
      className="w-full h-full object-cover"
      style={{ filter: "saturate(0.95) contrast(1.03)" }}
    />
  </div>
);

const buildColumnTiles = (seed) => {
  const src = MOSAIC_TITLES;
  if (src.length === 0) return [];
  const rotated = [...src.slice(seed % src.length), ...src.slice(0, seed % src.length)];
  const one = [];
  const times = 4;
  for (let i = 0; i < times; i++) one.push(...rotated);
  return [...one, ...one];
};

const Column = ({ seed, durationSec, direction, gap = 8 }) => {
  const tiles = buildColumnTiles(seed);
  const anim = direction === "up" ? "lr-col-up" : "lr-col-down";
  return (
    <div className="relative h-full overflow-hidden">
      <div
        className="absolute left-0 right-0 flex flex-col"
        style={{
          gap: `${gap}px`,
          animation: `${anim} ${durationSec}s linear infinite`,
          willChange: "transform",
        }}
      >
        {tiles.map((t, i) => (
          <Tile key={`${seed}-${i}`} t={t} />
        ))}
      </div>
    </div>
  );
};

// Permanent mosaic — fixed to the viewport so it becomes the visual language
// under every section of the homepage. Visible from mount; the Hero paints a
// black plate over it during the opening sequence.
export const PosterMosaic = () => {
  const columns = [
    { seed: 0, duration: 180, direction: "up" },
    { seed: 2, duration: 220, direction: "down" },
    { seed: 4, duration: 200, direction: "up" },
    { seed: 1, duration: 240, direction: "down" },
    { seed: 3, duration: 190, direction: "up" },
  ];

  return (
    <div
      aria-hidden
      data-testid="poster-mosaic"
      className="fixed inset-0 pointer-events-none select-none"
      style={{ zIndex: 0 }}
    >
      <div
        className="h-full w-full"
        style={{
          opacity: 0.9,
        }}
      >
        <div
          className="grid h-full w-full"
          style={{
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: "8px",
            padding: "0 6px",
          }}
        >
          {columns.map((c) => (
            <Column
              key={c.seed + "-" + c.direction}
              seed={c.seed}
              durationSec={c.duration}
              direction={c.direction}
            />
          ))}
        </div>
      </div>

      {/* Global darkening layer keeps the mosaic present but calm */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(8, 8, 8, 0.62)" }}
      />

      {/* Central vignette — darkens the middle of the viewport so headline
          copy from every section reads cleanly against the mosaic. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 55% at 50% 50%, rgba(8,8,8,0.55) 0%, rgba(8,8,8,0.25) 55%, rgba(8,8,8,0) 100%)",
        }}
      />
    </div>
  );
};

export default PosterMosaic;
